import { Editor, Node as TiptapNode } from '@tiptap/core';

export type IframeOptions = {
  src?: string;
  width?: string;
  height?: string;
  textAlign?: string;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iframe: {
      insertIframe: (options?: IframeOptions) => ReturnType;
    };
  }
}

const Iframe = TiptapNode.create({
  name: 'iframe',
  group: 'block',
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      src: { default: '' },
      width: { default: '560px' },
      height: { default: '315px' },
      textAlign: { default: 'left' },
    };
  },
  parseHTML() {
    return [{ tag: 'iframe' }];
  },
  renderHTML({ HTMLAttributes }) {
    const { textAlign, style, ...rest } = HTMLAttributes;
    let wrapperStyle = 'display: flex;';
    if (textAlign === 'center') {
      wrapperStyle += ' justify-content: center;';
    } else if (textAlign === 'right') {
      wrapperStyle += ' justify-content: flex-end;';
    } else {
      wrapperStyle += ' justify-content: flex-start;';
    }
    return ['div', { style: wrapperStyle }, ['iframe', { ...rest, style: style || '' }]];
  },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      let currentNode = node;
      const wrapper = document.createElement('div');
      wrapper.className = 'resizable-iframe-wrapper';
      wrapper.classList.add(`iframe-align-${node.attrs.textAlign || 'left'}`);
      wrapper.style.display = 'flex';
      wrapper.style.width = '100%';
      wrapper.setAttribute('contenteditable', 'false');
      if (node.attrs.textAlign === 'center') {
        wrapper.style.justifyContent = 'center';
      } else if (node.attrs.textAlign === 'right') {
        wrapper.style.justifyContent = 'flex-end';
      } else {
        wrapper.style.justifyContent = 'flex-start';
      }
      const iframeContainer = document.createElement('div');
      iframeContainer.classList.add('iframe-container');
      iframeContainer.style.display = 'inline-block';
      iframeContainer.style.position = 'relative';
      const iframeWidth = node.attrs.width?.toString().includes('px')
        ? node.attrs.width
        : `${node.attrs.width || 560}px`;
      const iframeHeight = node.attrs.height?.toString().includes('px')
        ? node.attrs.height
        : `${node.attrs.height || 315}px`;
      iframeContainer.style.setProperty('width', iframeWidth, 'important');
      iframeContainer.style.setProperty('height', iframeHeight, 'important');
      const iframe = document.createElement('iframe');
      iframe.src = node.attrs.src;
      iframe.style.display = 'block';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframeContainer.appendChild(iframe);
      const handlePositions = [
        'top-left',
        'top-middle',
        'top-right',
        'middle-left',
        'middle-right',
        'bottom-left',
        'bottom-middle',
        'bottom-right',
      ];
      const resizerHandles: HTMLDivElement[] = [];

      function startResize(event: PointerEvent, pos: string) {
        event.preventDefault();
        const startX = event.clientX;
        const startY = event.clientY;
        const startWidth = iframeContainer.offsetWidth;
        const startHeight = iframeContainer.offsetHeight;
        const aspect = startWidth / startHeight;

        function onMove(moveEvent: PointerEvent) {
          moveEvent.preventDefault();
          const diffX = moveEvent.clientX - startX;
          const diffY = moveEvent.clientY - startY;
          let newWidth = startWidth;
          let newHeight = startHeight;
          if (pos.includes('left')) {
            newWidth = startWidth - diffX;
          }
          if (pos.includes('right')) {
            newWidth = startWidth + diffX;
          }
          if (pos.includes('top')) {
            newHeight = startHeight - diffY;
          }
          if (pos.includes('bottom')) {
            newHeight = startHeight + diffY;
          }
          if (
            (pos.includes('top') || pos.includes('bottom')) &&
            (pos.includes('left') || pos.includes('right'))
          ) {
            newHeight = newWidth / aspect;
          }
          if (newWidth < 50) {
            newWidth = 50;
          }
          if (newHeight < 50) {
            newHeight = 50;
          }
          iframeContainer.style.width = `${newWidth}px`;
          iframeContainer.style.height = `${newHeight}px`;
        }

        function onUp(upEvent: PointerEvent) {
          upEvent.preventDefault();
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          iframe.style.pointerEvents = 'auto';
          const finalWidth = iframeContainer.style.width;
          const finalHeight = iframeContainer.style.height;
          editor.view.dom.focus({ preventScroll: true });
          editor
            .chain()
            .command(({ tr }) => {
              tr.setNodeMarkup(getPos(), undefined, {
                ...currentNode.attrs,
                width: finalWidth,
                height: finalHeight,
              });
              return true;
            })
            .run();
        }

        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
      }

      handlePositions.forEach((pos) => {
        const handle = document.createElement('div');
        handle.classList.add('resizer', pos);
        handle.style.display = 'none';
        handle.addEventListener('pointerdown', (e: PointerEvent) => {
          e.preventDefault();
          if (!['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(pos)) {
            return;
          }
          iframe.style.pointerEvents = 'none';
          startResize(e, pos);
        });
        iframeContainer.appendChild(handle);
        resizerHandles.push(handle);
      });

      wrapper.appendChild(iframeContainer);

      return {
        dom: wrapper,
        selectNode() {
          resizerHandles.forEach((handle) => {
            handle.style.display = 'block';
          });
        },
        deselectNode() {
          resizerHandles.forEach((handle) => {
            handle.style.display = 'none';
          });
        },
        update(updatedNode) {
          if (updatedNode.attrs.src !== currentNode.attrs.src) {
            iframe.src = updatedNode.attrs.src;
          }
          if (
            updatedNode.attrs.width !== currentNode.attrs.width ||
            updatedNode.attrs.height !== currentNode.attrs.height
          ) {
            iframeContainer.style.width = updatedNode.attrs.width;
            iframeContainer.style.height = updatedNode.attrs.height;
          }
          if (updatedNode.attrs.textAlign !== currentNode.attrs.textAlign) {
            wrapper.classList.remove(`iframe-align-${currentNode.attrs.textAlign || 'left'}`);
            wrapper.classList.add(`iframe-align-${updatedNode.attrs.textAlign || 'left'}`);
            if (updatedNode.attrs.textAlign === 'center') {
              wrapper.style.justifyContent = 'center';
            } else if (updatedNode.attrs.textAlign === 'right') {
              wrapper.style.justifyContent = 'flex-end';
            } else {
              wrapper.style.justifyContent = 'flex-start';
            }
          }
          currentNode = updatedNode;
          return true;
        },
      };
    };
  },
  addCommands() {
    return {
      insertIframe:
        (options?: IframeOptions) =>
        ({ commands }: { commands: Editor['commands'] }) => {
          return commands.insertContent({ type: this.name, attrs: options });
        },
    };
  },
});

export default Iframe;
